import React, { useEffect, useRef, useState } from "react";
import { Modal, Box, Button, Text, Stack, Alert, Group } from "@mantine/core";

interface BarcodeDetector {
  detect: (image: ImageBitmapSource) => Promise<Array<{ rawValue: string }>>;
}

declare global {
  interface Window {
    BarcodeDetector: {
      new (options?: { formats: string[] }): BarcodeDetector;
      getSupportedFormats: () => Promise<string[]>;
    };
  }
}

interface QrScannerModalProps {
  opened: boolean;
  onClose: () => void;
  onScan: (data: string) => void;
}

export function QrScannerModal({
  opened,
  onClose,
  onScan,
}: QrScannerModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [supported, setSupported] = useState<boolean>(false);
  const [scannedParts, setScannedParts] = useState<(string | null)[]>([]); // Array to hold parts (sparse if out of order)
  const [totalParts, setTotalParts] = useState<number | null>(null);
  const matchTotalPartsRef = useRef<number | null>(null);

  useEffect(() => {
    if ("BarcodeDetector" in window) {
      window.BarcodeDetector.getSupportedFormats().then((formats) => {
        if (formats.includes("qr_code")) {
          setSupported(true);
        } else {
          setError("QR code scanning is not supported by your device.");
        }
      });
    } else {
      setError("Barcode Detection API not available.");
    }
  }, []);

  useEffect(() => {
    if (!opened || !supported) return;

    let stream: MediaStream | null = null;
    let scanTimeoutId: ReturnType<typeof setTimeout>;
    const detector = new window.BarcodeDetector({ formats: ["qr_code"] });

    const startCamera = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
          scan();
        }
      } catch (err) {
        console.error(err);
        setError("Failed to access camera.");
      }
    };

    const scan = async () => {
      if (
        videoRef.current &&
        videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA
      ) {
        try {
          const barcodes = await detector.detect(videoRef.current);
          if (barcodes.length > 0) {
            const rawValue = barcodes[0].rawValue;
            handleScan(rawValue);
          }
        } catch (e) {
          console.error(e);
        }
      }
      // Scan every 100ms to avoid overwhelming the device
      scanTimeoutId = setTimeout(scan, 100);
    };

    startCamera();

    return () => {
      clearTimeout(scanTimeoutId);
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [opened, supported]);

  const handleScan = (data: string) => {
    // Check for pagination format: [Part/Total]Content
    // Regex: ^\[(\d+)\/(\d+)\](.*)
    const match = data.match(/^\[(\d+)\/(\d+)\](.*)/s); // 's' flag for dotAll to match newlines if any

    if (match) {
      const partIndex = parseInt(match[1]) - 1; // 0-indexed
      const total = parseInt(match[2]);
      const content = match[3];

      if (totalParts !== null && totalParts !== total) {
        // Total parts changed (different key or restart), reset state
        setTotalParts(total);
        setScannedParts(new Array(total).fill(null));
      } else if (totalParts === null) {
        setTotalParts(total);
      }

      setScannedParts((prev) => {
        let newParts = [...prev];

        // If we just reset (above), prev might be the old state due to closure,
        // but setState is batched. To be safe, we re-verify length.
        // Actually, doing a functional update after a setTotalParts might be tricky with closures.
        // Let's handle the reset logic purely inside this setter or ensure synchronization.

        // Simpler approach: handle reset inside here completely
        if (total !== matchTotalPartsRef.current) {
          newParts = new Array(total).fill(null);
          matchTotalPartsRef.current = total;
        }

        // Resize if needed (for initial scan)
        if (newParts.length < total) {
          for (let i = newParts.length; i < total; i++) newParts[i] = null;
        }

        if (newParts[partIndex] !== content) {
          newParts[partIndex] = content;
          // Check if complete
          if (newParts.length === total && newParts.every((p) => p !== null)) {
            onScan(newParts.join(""));
            onClose();
          }
        }
        return newParts;
      });
    } else {
      // Single part
      onScan(data);
      onClose();
    }
  };

  const resetScan = () => {
    setScannedParts([]);
    setTotalParts(null);
    matchTotalPartsRef.current = null;
  };

  useEffect(() => {
    if (opened) {
      resetScan();
    }
  }, [opened]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Scan QR Code"
      centered
      fullScreen
    >
      <Stack align="center" h="100%" justify="center">
        {error ? (
          <Alert color="red">{error}</Alert>
        ) : (
          <>
            <Box
              style={{
                position: "relative",
                width: "100%",
                maxWidth: 400,
                aspectRatio: "1/1",
                overflow: "hidden",
                borderRadius: 8,
                background: "black",
              }}
            >
              <video
                ref={videoRef}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                playsInline
                muted
              />
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "80%",
                  height: "80%",
                  border: "2px solid rgba(255,255,255,0.5)",
                  borderRadius: 12,
                }}
              />
            </Box>

            {totalParts !== null && (
              <Stack align="center" gap={4}>
                <Text fw={700}>
                  Scanning Part {scannedParts.filter((p) => p !== null).length}{" "}
                  / {totalParts}
                </Text>
                <Text size="xs" c="dimmed">
                  Please scan all parts to complete import.
                </Text>
                <Group gap={2}>
                  {Array.from({ length: totalParts }).map((_, i) => (
                    <Box
                      key={i}
                      w={20}
                      h={6}
                      bgsz="cover"
                      bg={scannedParts[i] ? "green" : "gray.3"}
                      style={{ borderRadius: 2 }}
                    />
                  ))}
                </Group>
              </Stack>
            )}

            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  );
}
